import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LessonReport } from '~/core/services/http.service';

interface ParsedPattern {
  number: number;
  title: string;
  whatHappened: string;
  watchFor: string[];
  rootCause: string;
  preventBy: string[];
  ifItHappens: string[];
  fullText: string;
}

interface ParsedLesson {
  wellId: string;
  wellName: string;
  patterns: ParsedPattern[];
  expanded: boolean;
  fullContentExpanded: boolean;
  markdownHtml: SafeHtml;
}

@Component({
  selector: 'app-lessons-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lessons-tab.component.html',
  styleUrls: ['./lessons-tab.component.scss'],
})
export class LessonsTabComponent implements OnChanges {
  @Input() isMultiWellMode = false;
  @Input() LESSONS: any[] = [];
  @Input() multiWellLessonsByWell: { wellName: string; lessons: any[] }[] = [];
  @Input() apiLessonsReport: string = '';
  @Input() apiLessonsLoading = false;
  @Input() apiLessonsError: string | null = null;
  @Input() apiLessonsData: any = null;

  // Stored lessons for proper change detection and state management
  storedParsedLessons: ParsedLesson[] = [];

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['apiLessonsData'] && this.apiLessonsData) {
      this.updateParsedLessons();
    }
  }

  /**
   * Update parsed lessons from API data
   */
  private updateParsedLessons(): void {
    if (!this.apiLessonsData || !this.apiLessonsData.reports) {
      this.storedParsedLessons = [];
      return;
    }

    const lessons: ParsedLesson[] = [];

    // Process each report from the reports array
    this.apiLessonsData.reports.forEach((reportObj: LessonReport) => {
      const wellId = reportObj.well_id;
      const wellName = reportObj.well_name;
      const reportText = reportObj.report;
      const metadata = reportObj.metadata as any;

      // Check if this is a "No NPT events found" report
      const isNoNPTReport = reportText && reportText.includes('No NPT events found');

      // Check if this is an asset fallback with similar wells
      const isAssetFallback = metadata?.retrieval_strategy === 'asset_fallback_no_npt_match';

      // If it's a fallback with similar wells, convert the table but keep all content
      if (wellId && isAssetFallback && reportText) {
        const htmlContent = this.convertReportWithTableFormatting(reportText);
        lessons.push({
          wellId,
          wellName,
          patterns: [],
          expanded: !this.isMultiWellMode,
          fullContentExpanded: false,
          markdownHtml: htmlContent
        });
      } else if (wellId && reportText && !isNoNPTReport) {
        // Normal processing for regular lessons
        const patterns = this.extractPatternsFromReport(reportText);
        const markdownHtml = this.markdownToHtml(reportText);
        lessons.push({ wellId, wellName, patterns, expanded: !this.isMultiWellMode, fullContentExpanded: false, markdownHtml });
      } else if (wellId && isNoNPTReport) {
        // For "No NPT events found" reports, show a simple message
        const simpleMessage = `<strong>No NPT events found for this well.</strong>`;
        lessons.push({
          wellId,
          wellName,
          patterns: [],
          expanded: !this.isMultiWellMode,
          fullContentExpanded: false,
          markdownHtml: this.sanitizer.bypassSecurityTrustHtml(simpleMessage)
        });
      }
    });

    this.storedParsedLessons = lessons;
  }

  /**
   * Convert report with table formatting for Similar Historical Wells
   */
  private convertReportWithTableFormatting(reportText: string): SafeHtml {
    let html = reportText;

    // Convert markdown tables to HTML tables FIRST (before line breaks)
    html = this.convertAllMarkdownTablesToHtml(html);

    // Convert note section (blockquote)
    html = html.replace(/^> (.+)$/gm, '<blockquote style="border-left: 4px solid #f59e0b; background-color: #fffbeb; padding: 1rem; margin: 1rem 0; border-radius: 0.375rem; color: #92400e;">$1</blockquote>');

    // Convert headers - reduce font size for Similar Historical Wells
    html = html.replace(/^## Similar Historical Wells$/gm, '<strong style="font-size: 0.95rem; display: block; margin-top: 1rem; margin-bottom: 0.5rem;">Similar Historical Wells</strong>');
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<strong style="font-size: 1.125rem; display: block; margin-top: 1rem; margin-bottom: 0.5rem;">$1</strong>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Convert bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Convert horizontal rule
    html = html.replace(/^---\s*$/gm, '<hr style="border: none; height: 1px; background: #e5e7eb; margin: 1rem 0;">');

    // Convert line breaks AFTER table conversion
    html = html.replace(/\n\n+/g, '<br>');
    html = html.replace(/\n/g, '<br>');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Convert all markdown tables in text to HTML tables
   * This must be done BEFORE converting line breaks to <br> tags
   * Handles separators with 2+ dashes per column: --|--|, ---|---|, ----|---|, etc.
   * Handles tables with or without trailing newline on last row
   */
  private convertAllMarkdownTablesToHtml(text: string): string {
    // Match markdown table: header row, separator row, and data rows
    // Pattern breakdown:
    // \| - pipe at start
    // .+ - header content
    // \n - newline after header
    // \| - pipe for separator row
    // [\s\-:|]+ - separator chars (spaces, dashes, colons, pipes)
    // \n - newline after separator
    // (?:\|.+(?:\n|$))* - zero or more data rows (with newline OR end of string)
    const tablePattern = /\|.+\n\|[\s\-:|]+\n(?:\|.+(?:\n|$))*/g;
    
    const matches = [...text.matchAll(tablePattern)];
    
    return text.replace(tablePattern, (match) => {
      // Verify it's actually a table before converting
      const lines = match.trim().split('\n');
      if (lines.length < 2) {
        return match;
      }
      
      // Check if second line is a separator (contains dashes and pipes)
      const isSeparator = /^[|\s\-:|]+$/.test(lines[1]);
      if (!isSeparator) {
        return match;
      }
      
      const result = this.markdownTableToHtml(match.trim());
      return result + '\n';
    });
  }

  /**
   * Extract and format Similar Historical Wells table from markdown
   */
  private extractAndFormatSimilarWells(reportText: string): SafeHtml {
    // Look for the "Similar Historical Wells" section with the markdown table
    const similarWellsMatch = reportText.match(/## Similar Historical Wells\n\n([\s\S]*?)(?=##|$)/);
    
    if (similarWellsMatch && similarWellsMatch[1]) {
      const tableText = similarWellsMatch[1].trim();
      // Convert markdown table to HTML table
      const htmlTable = this.markdownTableToHtml(tableText);
      return this.sanitizer.bypassSecurityTrustHtml(htmlTable);
    }

    // If no similar wells found, return empty
    return this.sanitizer.bypassSecurityTrustHtml('');
  }

  /**
   * Get parsed lessons (from stored property for proper change detection)
   */
  get parsedLessons(): ParsedLesson[] {
    return this.storedParsedLessons;
  }

  /**
   * Extract patterns from a single well's markdown report
   * Dynamically extracts any bold text (**...**) as section headers
   */
  private extractPatternsFromReport(reportText: string): ParsedPattern[] {
    const patterns: ParsedPattern[] = [];
    const lines = reportText.split('\n');

    let i = 0;
    let patternCount = 0;
    
    while (i < lines.length) {
      const line = lines[i].trim();

      // Look for ANY bold text: **anything** (dynamically handles any section header)
      const boldMatch = line.match(/^\*\*(.+?)\*\*$/);
      if (boldMatch) {
        patternCount++;
        const sectionTitle = boldMatch[1];
        
        // Collect all content for this section
        const sectionLines: string[] = [];
        i++;

        // Gather lines until we hit the next bold section or end of text
        while (i < lines.length && !lines[i].trim().match(/^\*\*.+?\*\*$/)) {
          sectionLines.push(lines[i]);
          i++;
        }

        // Parse the collected lines into structured sections
        const parsed = this.parsePatternSections(sectionLines);
        
        patterns.push({
          number: patternCount,
          title: sectionTitle,
          whatHappened: parsed.whatHappened,
          watchFor: parsed.watchFor,
          rootCause: parsed.rootCause,
          preventBy: parsed.preventBy,
          ifItHappens: parsed.ifItHappens,
          fullText: sectionLines.join('\n').trim()
        });
      } else {
        i++;
      }
    }

    return patterns;
  }

  /**
   * Parse pattern content into structured sections
   */
  private parsePatternSections(lines: string[]): any {
    const result = {
      whatHappened: '',
      watchFor: [] as string[],
      rootCause: '',
      preventBy: [] as string[],
      ifItHappens: [] as string[]
    };

    let currentSection = '';
    let currentContent: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();

      // Detect section headers
      if (trimmed.startsWith('**What happened:**')) {
        if (currentSection && currentContent.length > 0) {
          this.assignSection(result, currentSection, currentContent);
        }
        currentSection = 'whatHappened';
        currentContent = [];
        const content = trimmed.replace('**What happened:**', '').trim();
        if (content) currentContent.push(content);
      } else if (trimmed.startsWith('**Watch for:**')) {
        if (currentSection && currentContent.length > 0) {
          this.assignSection(result, currentSection, currentContent);
        }
        currentSection = 'watchFor';
        currentContent = [];
      } else if (trimmed.startsWith('**Root cause:**')) {
        if (currentSection && currentContent.length > 0) {
          this.assignSection(result, currentSection, currentContent);
        }
        currentSection = 'rootCause';
        currentContent = [];
        const content = trimmed.replace('**Root cause:**', '').trim();
        if (content) currentContent.push(content);
      } else if (trimmed.startsWith('**Prevent by:**')) {
        if (currentSection && currentContent.length > 0) {
          this.assignSection(result, currentSection, currentContent);
        }
        currentSection = 'preventBy';
        currentContent = [];
      } else if (trimmed.startsWith('**If it happens:**')) {
        if (currentSection && currentContent.length > 0) {
          this.assignSection(result, currentSection, currentContent);
        }
        currentSection = 'ifItHappens';
        currentContent = [];
      } else if (trimmed === '---' || trimmed === '') {
        // Skip separators and empty lines
        continue;
      } else if (currentSection) {
        // Add content to current section
        if (trimmed.startsWith('- ')) {
          currentContent.push(trimmed.substring(2));
        } else if (trimmed) {
          currentContent.push(trimmed);
        }
      }
    }

    // Don't forget the last section
    if (currentSection && currentContent.length > 0) {
      this.assignSection(result, currentSection, currentContent);
    }

    return result;
  }

  /**
   * Assign parsed content to appropriate section
   */
  private assignSection(result: any, section: string, content: string[]): void {
    if (section === 'whatHappened') {
      result.whatHappened = content.join(' ').trim();
    } else if (section === 'watchFor') {
      result.watchFor = content.filter(c => c.length > 0);
    } else if (section === 'rootCause') {
      result.rootCause = content.join(' ').trim();
    } else if (section === 'preventBy') {
      result.preventBy = content.filter(c => c.length > 0);
    } else if (section === 'ifItHappens') {
      result.ifItHappens = content.filter(c => c.length > 0);
    }
  }

  /**
   * Toggle full content expansion for a well (inline display, not modal)
   */
  toggleFullContent(lesson: ParsedLesson, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    lesson.fullContentExpanded = !lesson.fullContentExpanded;
  }

  /**
   * Get truncated markdown content (first 400 characters)
   */
  getTruncatedMarkdown(lesson: ParsedLesson): SafeHtml {
    // If there's already HTML content, truncate it
    if (lesson.markdownHtml) {
      // Get the text content from the HTML for truncation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = (lesson.markdownHtml as any).changingThisBreaksApplicationSecurity || '';
      const textContent = tempDiv.textContent || '';
      
      // Truncate to first 400 characters
      const truncated = textContent.substring(0, 400);
      const needsMore = textContent.length > 400;
      
      // Escape HTML meta-characters before trusting
      const escapedTruncated = this.escapeHtml(truncated + (needsMore ? '...' : ''));
      
      // Convert back to HTML with the truncated content
      return this.sanitizer.bypassSecurityTrustHtml(escapedTruncated);
    }

    // Fallback to pattern-based truncation
    if (!lesson.patterns || lesson.patterns.length === 0) return this.sanitizer.bypassSecurityTrustHtml('');

    // Get the raw text from first pattern's full text
    let rawText = '';
    lesson.patterns.forEach((pattern, index) => {
      rawText += pattern.fullText;
      if (index < lesson.patterns.length - 1) {
        rawText += '\n\n---\n\n';
      }
    });

    // Truncate to first 400 characters
    const truncated = rawText.substring(0, 400);
    const needsMore = rawText.length > 400;

    // Convert to HTML
    let html = this.convertMarkdownToHtml(truncated + (needsMore ? '...' : ''));
    
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Convert markdown to HTML (simple version for truncated content)
   */
  private convertMarkdownToHtml(markdown: string): string {
    let html = markdown;

    // Convert headers
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<strong>$1</strong>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Convert bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Convert line breaks
    html = html.replace(/\n\n/g, '<br><br>');
    html = html.replace(/\n/g, '<br>');

    return html;
  }

  /**
   * Get preview content (pattern titles and "What happened" only)
   */
  getPreviewContent(lesson: ParsedLesson): SafeHtml {
    if (!lesson.patterns || lesson.patterns.length === 0) return this.sanitizer.bypassSecurityTrustHtml('');

    let html = '';

    lesson.patterns.forEach((pattern, index) => {
      html += `<h3>${pattern.title}</h3>`;
      html += `<p><strong>What happened:</strong></p>`;
      html += `<p>${pattern.whatHappened}</p>`;
      
      if (index < lesson.patterns.length - 1) {
        html += `<hr>`;
      }
    });

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Format markdown text for display (convert bold, etc.)
   */
  formatLessonText(text: string): string {
    if (!text) return '';
    // Remove code blocks for display
    text = text.replace(/```[\s\S]*?```/g, '');
    // Convert markdown bold to plain text (just strip the **)
    text = text.replace(/\*\*(.+?)\*\*/g, '$1');
    return text.trim();
  }

  /**
   * Convert markdown to HTML for display
   */
  markdownToHtml(markdown: string): SafeHtml {
    if (!markdown) return this.sanitizer.bypassSecurityTrustHtml('');
    
    let html = markdown;

    // FIRST: Convert markdown tables to HTML tables (before line breaks)
    html = this.convertAllMarkdownTablesToHtml(html);

    // Convert headers: ### Title -> <h3>Title</h3>
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<strong>$1</strong>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Convert bold: **text** -> <strong>text</strong>
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Convert horizontal rule: --- -> <hr>
    html = html.replace(/^---\s*$/gm, '<hr>');

    // Convert bullet lists: - item -> <ul><li>item</li></ul>
    const lines = html.split('\n');
    let result = [];
    let inList = false;

    for (const line of lines) {
      if (line.trim().startsWith('- ')) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        const item = line.trim().substring(2);
        result.push(`<li>${item}</li>`);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(line);
      }
    }
    if (inList) {
      result.push('</ul>');
    }

    html = result.join('\n');

    // Convert line breaks: \n\n+ -> single <br> (reduced spacing)
    html = html.replace(/\n\n+/g, '<br><br>');

    // Convert remaining single line breaks
    html = html.replace(/\n/g, '<br>');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Convert markdown table to HTML table
   */
  private markdownTableToHtml(markdownTable: string): string {
    const lines = markdownTable.split('\n').filter(line => line.trim().length > 0);
    if (lines.length < 3) {
      return markdownTable;
    }

    const parseTableRow = (row: string): string[] => {
      // Split by | and filter out empty cells
      return row.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
    };

    const isSeparatorRow = (row: string): boolean => {
      // Check if row contains only |, -, :, and spaces
      const cleaned = row.replace(/[|\s:-]/g, '');
      return cleaned.length === 0;
    };

    // Find header and separator rows
    let headerRowIndex = 0;
    let separatorRowIndex = 1;

    // Find the separator row (contains dashes)
    for (let i = 1; i < lines.length; i++) {
      if (isSeparatorRow(lines[i])) {
        headerRowIndex = i - 1;
        separatorRowIndex = i;
        break;
      }
    }

    const headerCells = parseTableRow(lines[headerRowIndex]);
    const columnCount = headerCells.length;

    let html = '<table style="width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.875rem;">';

    // Add header row
    html += '<thead style="background-color: #f3f4f6; border-bottom: 2px solid #e5e7eb;"><tr>';
    headerCells.forEach(cell => {
      html += `<th style="padding: 0.625rem; text-align: left; font-weight: 600; color: #1f2937; font-size: 0.8125rem; word-break: break-word;">${cell}</th>`;
    });
    html += '</tr></thead>';

    // Add data rows
    html += '<tbody>';
    for (let i = separatorRowIndex + 1; i < lines.length; i++) {
      const cells = parseTableRow(lines[i]);
      if (cells.length > 0) {
        html += '<tr style="border-bottom: 1px solid #e5e7eb;">';
        // Ensure we have the right number of cells
        for (let j = 0; j < columnCount; j++) {
          const cellValue = cells[j] || '';
          html += `<td style="padding: 0.625rem; color: #4b5563; font-size: 0.8125rem; word-break: break-word;">${cellValue}</td>`;
        }
        html += '</tr>';
      }
    }
    html += '</tbody></table>';

    return html;
  }

  /**
   * Escape HTML meta-characters to prevent injection attacks
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
